import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { NavigationHistoryService } from '../../services/navigation-history.service';
import { APP_ICONS } from '../../design/icons';

function toPascal(kebab: string): string {
  return kebab
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

@Component({
  selector: 'ss-page-header',
  template: `
    <header class="ss-page-header" [class.ss-page-header--detail]="isDetail">
      <div class="ss-page-header__main">
        <a
          *ngIf="backLink"
          class="ss-page-header__back"
          [routerLink]="resolvedBackLink"
          [attr.aria-label]="backLabel"
          [attr.title]="backLabel">
          <lucide-icon name="chevron-left" [size]="20" [strokeWidth]="2.4" aria-hidden="true"></lucide-icon>
        </a>
        <button
          *ngIf="!backLink && showBackButton"
          type="button"
          class="ss-page-header__back"
          [attr.aria-label]="backLabel"
          [attr.title]="backLabel"
          (click)="back.emit()">
          <lucide-icon name="chevron-left" [size]="20" [strokeWidth]="2.4" aria-hidden="true"></lucide-icon>
        </button>

        <span *ngIf="resolvedIcon" class="ss-page-header__tile" aria-hidden="true">
          <lucide-icon [name]="resolvedIcon" [size]="22" [strokeWidth]="2"></lucide-icon>
        </span>

        <div class="ss-page-header__heading">
          <h1 class="ss-page-header__title">{{ title }}</h1>
          <span *ngIf="contextText" class="ss-page-header__context">{{ contextText }}</span>
        </div>
      </div>

      <div class="ss-page-header__actions">
        <ng-content></ng-content>
      </div>
    </header>
  `,
  standalone: false
})
export class PageHeaderComponent {
  @Input() title = '';
  @Input() icon = '';
  @Input() subtitle = '';
  @Input() context: string | string[] = '';
  @Input() backLink: string | any[] | null = null;
  @Input() backQueryParams: Record<string, any> | null = null;
  @Input() showBackButton = false;
  @Input() backLabel = 'Voltar';
  @Input() variant: 'auto' | 'module' | 'detail' = 'auto';

  @Output() back = new EventEmitter<void>();

  constructor(private router: Router, private navigationHistoryService: NavigationHistoryService) {}

  get resolvedBackLink(): UrlTree | null {
    if (!this.backLink) return null;
    const target = typeof this.backLink === 'string' ? this.router.parseUrl(this.backLink)
      : this.router.createUrlTree(this.backLink);
    if (this.backQueryParams !== null) {
      target.queryParams = this.backQueryParams;
      return target;
    }
    return this.navigationHistoryService.restore(target);
  }

  get isDetail(): boolean {
    if (this.variant === 'detail') return true;
    if (this.variant === 'module') return false;
    return Boolean(this.backLink || this.showBackButton || this.contextText);
  }

  get resolvedIcon(): string {
    if (!this.icon) return '';
    const raw = this.icon.trim();
    return toPascal(raw) in APP_ICONS ? raw : 'circle-question-mark';
  }

  get contextText(): string {
    if (Array.isArray(this.context)) {
      return this.context.filter(Boolean).join(' · ');
    }
    return this.context || this.subtitle || '';
  }
}
